import { Component, computed, effect, inject, input, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	Day,
	IMDDropDownOption,
	MDAlertService,
	MDDatePickerInputDirective,
	MDDialogModule,
	MDDialogRef,
	MDDropDownComponent,
	MDIconComponent,
	MD_DIALOG_REF
} from '@melodic-ui/components';
import { OverlayContainer } from '@angular/cdk/overlay';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, merge, take } from 'rxjs';
import { isBefore, startOfDay, format } from 'date-fns';
import { CoreRescheduleService } from '../../../api/core/services/core-reschedule.service';
import {
	ICommitRescheduleRequest,
	IRescheduleCheckResponse,
	IRescheduleOptionsResponse,
	RescheduleInitiator,
	RescheduleSlotGroupType
} from '../../../api/core/interfaces';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { LoaderComponent } from '../../loader/loader.component';
import { FormRowComponent } from '../../form/form-row/form-row.component';
import { FormFieldComponent } from '../../form/form-field/form-field.component';
import { AuthenticationService } from '../../../auth/services/authentication.service';
import { GlobalTimezoneService } from '../../../services/global-timezone.service';
import { convertLessonDateForDisplay, convertUserInputToUTC } from '../../../functions/timezone-utils';
import { getTimeOptions } from '../../../functions/get-time-options.function';

type RescheduleOption = 'learner' | 'afterLastDate' | 'time';

@Component({
	selector: 'lb-instructor-reschedule-lesson-dialog-v2',
	standalone: true,
	imports: [
		DatePipe,
		FormsModule,
		ReactiveFormsModule,
		MDIconComponent,
		MDDialogModule,
		MDDatePickerInputDirective,
		MDDropDownComponent,
		FormRowComponent,
		FormFieldComponent,
		LoaderComponent
	],
	templateUrl: './instructor-reschedule-lesson-dialog-v2.component.html',
	styleUrl: './instructor-reschedule-lesson-dialog-v2.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class InstructorRescheduleLessonDialogV2Component implements OnInit {
	private _overlayContainer: OverlayContainer = inject(OverlayContainer);
	private dialogRef: MDDialogRef = inject(MD_DIALOG_REF);
	private _rescheduleService = inject(CoreRescheduleService);
	private _authService = inject(AuthenticationService);
	private _alertService = inject(MDAlertService);
	private _globalTimezoneService = inject(GlobalTimezoneService);

	private _times = getTimeOptions(0, 24, 30).map((opt) => ({ value: opt.id, label: opt.label }) as IMDDropDownOption);
	private _selectedTimezone = this._globalTimezoneService.selectedTimezone;

	lesson = input<ILessonListItem>();

	// Load state
	optionsLoading = signal(false);
	options = signal<IRescheduleOptionsResponse | null>(null);

	// Submit state
	rescheduling = signal(false);

	// Check state for "Enter new time"
	checking = signal(false);
	checkResult = signal<IRescheduleCheckResponse | null>(null);

	// Form controls
	rescheduleOption = new FormControl<RescheduleOption>('afterLastDate');
	rescheduleDate = new FormControl(format(new Date(), 'yyyy-MM-dd'));
	rescheduleTime = new FormControl(this._times[0].value);

	timesOptions = signal<IMDDropDownOption[]>(this._times);

	/** Signal-wrapped form control value so computed()s track it reactively. */
	rescheduleOptionValue = toSignal(this.rescheduleOption.valueChanges, { initialValue: this.rescheduleOption.value as RescheduleOption });

	isAdmin = computed(() => this._authService.hasEntitlement('Admin:App:Access'));

	/** Admins share this dialog via the dispatcher — report the true initiator so provenance and notifications credit the acting user, not the instructor. */
	private initiator = computed(() => (this.isAdmin() ? RescheduleInitiator.Admin : RescheduleInitiator.Instructor));

	proposingNewDate = computed(() => this.rescheduleOptionValue() === 'time');

	afterLastSessionSlot = computed(() => {
		const groups = this.options()?.groups ?? [];
		return groups.find((g) => g.type === RescheduleSlotGroupType.AfterLastSession)?.slots[0] ?? null;
	});

	/** True once options have loaded and there is no AfterLastSession slot (no recurring schedule). */
	afterLastUnavailable = computed(() => !this.optionsLoading() && this.options() !== null && this.afterLastSessionSlot() === null);

	/** Slot date converted to the slot's enrollment timezone for display, falling back to the user's selected timezone. */
	afterLastSessionDisplayDate = computed(() => {
		const slot = this.afterLastSessionSlot();
		if (!slot) return null;
		const date = new Date(slot.date);
		const tz = slot.timezoneId
			? this._globalTimezoneService.availableTimezones().find((t) => t.timeZoneID === slot.timezoneId) ?? this._selectedTimezone()
			: this._selectedTimezone();
		return convertLessonDateForDisplay(date, tz);
	});

	canSubmit = computed(() => {
		if (this.rescheduling()) return false;
		const option = this.rescheduleOptionValue();
		if (option === 'time') {
			return this.checkResult()?.isValid === true;
		}
		if (option === 'afterLastDate') {
			return !this.optionsLoading() && this.afterLastSessionSlot() !== null;
		}
		return true; // 'learner'
	});

	constructor() {
		// Reset check result when option changes
		this.rescheduleOption.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
			this.checkResult.set(null);
		});

		// Auto-redirect away from 'afterLastDate' if no slot is available once loading completes
		effect(() => {
			if (this.afterLastUnavailable() && this.rescheduleOptionValue() === 'afterLastDate') {
				this.rescheduleOption.setValue('time');
			}
		});

		// Re-run /check whenever date or time changes (debounced)
		merge(this.rescheduleDate.valueChanges, this.rescheduleTime.valueChanges)
			.pipe(debounceTime(500), takeUntilDestroyed())
			.subscribe(() => {
				if (this.rescheduleOptionValue() === 'time') {
					this.runCheck();
				}
			});
	}

	ngOnInit(): void {
		const overlayContainerElement = this._overlayContainer.getContainerElement();
		if (overlayContainerElement.firstChild) {
			overlayContainerElement.removeChild(overlayContainerElement.firstChild);
		}

		// Pre-populate date/time from current lesson
		const lesson = this.lesson();
		const selectedTimezone = this._selectedTimezone();

		if (lesson?.lessonDate) {
			const lessonStartDate = new Date(lesson.lessonDate);
			const lessonDate = selectedTimezone ? convertLessonDateForDisplay(lessonStartDate, selectedTimezone) : lessonStartDate;
			let lessonStartTime = format(lessonDate, 'HH:mm');
			if (lessonStartTime[0] === '0') lessonStartTime = lessonStartTime.slice(1);
			this.rescheduleDate.setValue(format(lessonDate, 'yyyy-MM-dd'), { emitEvent: false });
			this.rescheduleTime.setValue(lessonStartTime, { emitEvent: false });
		}

		// Load options to get AfterLastSession computed date
		if (lesson) {
			this.optionsLoading.set(true);
			this._rescheduleService
				.getOptions(lesson.lessonID, this.initiator())
				.pipe(take(1))
				.subscribe({
					next: (response) => {
						this.options.set(response);
						this.optionsLoading.set(false);
					},
					error: () => this.optionsLoading.set(false)
				});
		}
	}

	disabledDates(): (day: Day) => boolean {
		return (day: Day) => (this.isAdmin() ? false : isBefore(day.date, startOfDay(new Date())));
	}

	private runCheck(): void {
		const lesson = this.lesson();
		if (!lesson) return;

		let time = this.rescheduleTime.value as string;
		if (!time || !this.rescheduleDate.value) return;

		if (time.length === 4) time = '0' + time;

		const selectedTimezone = this._selectedTimezone();
		const utcDate = convertUserInputToUTC(this.rescheduleDate.value!, time, selectedTimezone);
		if (!utcDate) return;

		this.checking.set(true);
		this.checkResult.set(null);

		this._rescheduleService
			.check(lesson.lessonID, { proposedDate: utcDate.toISOString() }, this.initiator())
			.pipe(take(1))
			.subscribe({
				next: (result) => {
					this.checkResult.set(result);
					this.checking.set(false);
				},
				error: () => {
					this.checking.set(false);
				}
			});
	}

	reschedule(): void {
		const lesson = this.lesson();
		if (!lesson || !this.canSubmit()) return;

		this.rescheduling.set(true);

		const option = this.rescheduleOption.value as RescheduleOption;

		let proposedDate: string | null = null;
		let propose = false;

		if (option === 'learner') {
			propose = true;
		} else if (option === 'time') {
			let time = this.rescheduleTime.value as string;
			if (time.length === 4) time = '0' + time;
			const selectedTimezone = this._selectedTimezone();
			const utcDate = convertUserInputToUTC(this.rescheduleDate.value ?? '', time, selectedTimezone);
			proposedDate = utcDate ? utcDate.toISOString() : null;
		}
		// 'afterLastDate' → proposedDate stays null, propose stays false

		const request: ICommitRescheduleRequest = {
			lessonID: lesson.lessonID,
			proposedDate,
			propose,
			initiatedBy: this.initiator()
		};

		this._rescheduleService
			.commit(lesson.lessonID, request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.rescheduling.set(false);
					this.dialogRef.close(true);
				},
				error: (error) => {
					this.rescheduling.set(false);
					this._alertService.addAlert({
						type: 'error',
						title: 'Reschedule Failed',
						message: error?.error?.message || 'Something went wrong rescheduling the lesson.',
						duration: 4000
					});
				}
			});
	}

	close(): void {
		this.dialogRef.close();
	}
}
